import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";
import { useAccessToken } from "../useAccessToken";

export const companyKeys = {
  companies: (pageNo: number, filter: string) =>
    ["companies", pageNo, filter] as const,
  company: (companyId: string) => ["company", companyId] as const,
  companyUsers: (companyId: string) => ["companyUsers", companyId] as const,
};

export type CompanyData = BaseCompanyData & {
  noOfProjects: {
    inProgress: number;
    paused: number;
    completed: number;
    canceled: number;
  };
  projectLatestCreatedAt?: string;
};

type BaseCompanyData = {
  _id: string;
  name: string;
  source?: "hubspot" | "salesforce";
  crmId?: string;
  createdAt: string;
  updatedAt: string;
};

export type PaginatedCompaniesData = {
  companies: CompanyData[];
  totalNoOfPages: number;
  currentPageNo: number;
};

export type CompanyUser = {
  _id: string;
  name: string;
  email: string;
};

export const useGetUsersForCompany = (companyId: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    companyKeys.companyUsers(companyId),
    async (): Promise<CompanyUser[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/usersForCustomer`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            customerId: companyId,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: 1000 * 60 * 2,
    }
  );
};
export const useGetCompanies = (pageNo: number, filter: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    companyKeys.companies(pageNo, filter),
    async (): Promise<PaginatedCompaniesData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/companies`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            filter,
            pageNo,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: 1000 * 60 * 2,
    }
  );
};

export const useGetCompany = (_id: string) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    companyKeys.company(_id),
    async (): Promise<CompanyData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/company`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            _id,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: 1000 * 60 * 2,
    }
  );
};

export const useDeleteCompany = () => {
  const app = useRealmApp();
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (company: {
      _id: string;
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.delete(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/customer`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          params: company,
        }
      );
      return res.data;
    },
    onError: (err: AxiosError) => {
      switch (err.response?.status) {
        case 403:
          snackbarCtx.showSnackbar(
            "Cannot delete companies imported from your CRM.",
            "error"
          );
          break;
        case 409:
          snackbarCtx.showSnackbar(
            "Company cannot be deleted since it has associated projects.",
            "error"
          );
          break;
        default:
          snackbarCtx.showSnackbar("Error deleting company", "error");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["companies"]);
      navigate(`/companies`);
    },
  });
};
